import React, { useEffect, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import { format } from 'date-fns';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useQuery } from 'urql';

import { Button } from '@/components';
import Breadcrumbs from '@/components/Breadcrumbs';
import ClaimOfferBlock from '@/components/ClaimOfferBlock';
import GalleryCarousel from '@/components/GalleryCarousel';
import HeroImage from '@/components/layout/HeroImage';
import LocationCard from '@/components/LocationCard';
import OfferCard from '@/components/OfferCard';
import PlaceHolderImage from '@/components/PlaceHolderImage';
import ShareBlock from '@/components/ShareBlock';
import useIsMobile from '@/hooks/useIsMobile';
import useStore from '@/hooks/useStore';
import { getServerSidePropsWithGlobalData } from '@/lib/getServerSideProps';
import { extractFragment, extractItems } from '@/lib/GraphQLHelper';
import { withUrql } from '@/lib/urqlClient';
import { ErrorPage } from '@/pages/_error';

import CouponIcon from '@/images/icons/coupon.svg';
import DownloadIcon from '@/images/icons/pdf.svg';

import query from './query.graphql';
import relatedQuery from './related.graphql';

import styles from './styles.module.scss';

const Offer = () => {
  const { exclusive, loggedIn } = useStore('userStore');
  const [isMobile] = useIsMobile(768);

  const router = useRouter();
  let galleryImages: ImageObject[];

  const [{ data, error, fetching }] = useQuery<OfferQuery>({
    query,
    variables: {
      slug: router.query.slug,
      exclusive,
    },
  });

  const offerEntry = extractFragment<OfferFragment>(data?.entry);
  const interestEntries = extractItems<InterestFragment>(offerEntry?.relatedInterests);
  const categoryEntries = extractItems<CategoryFragment>(offerEntry?.categories);

  const interests = interestEntries?.map((i: Maybe<InterestFragment>) => i?.slug).toString() || '*';

  const [{ data: relatedEntries }] = useQuery({
    query: relatedQuery,
    pause: fetching,
    // hide exclusive offers if not logged in, don't show the page offer based on slug
    // show offer with a matching category, show offers with matching interests
    variables: {
      slug: ['not', router?.query?.slug],
      exclusive,
      search: `${offerEntry?.title?.split(' ')?.join(' OR ')} ${
        router?.query?.category && ` OR categories:${router?.query?.category}`
      } ${interests && ` OR relatedInterests:${interests}`}`,
    },
  });

  const breadcrumbs: BreadcrumbItem[] = [
    { title: 'Benefits & Offers', uri: '/offers' },
    { title: categoryEntries?.[0]?.title || '', uri: `/offers/${categoryEntries?.[0]?.slug}` || '' },
    { title: offerEntry?.title || '' },
  ];

  const similarEntries = useMemo(() => {
    if (relatedEntries) {
      // prettier-ignore
      const search = relatedEntries.entries;
      // If there are forced related offers show these before returing the related
      if (offerEntry?.offers?.length) {
        const offersEntries = extractItems<RelatedProductOfferFragment | RelatedOfferFragment>(offerEntry.offers);
        return offersEntries?.map((e) => <OfferCard offer={e as OfferFragment} key={e!.slug} />).slice(0, 3);
      }
      return search.map((e: OfferFragment) => <OfferCard offer={e} key={e.slug} />);
    }
    return [];
  }, [relatedEntries, offerEntry?.offers]);

  useEffect(() => {
    // reroutes user if there is no data, they are not logged in, they are not fetching data
    // & there are no offers
    if (!offerEntry && !loggedIn && !fetching) {
      window.location.assign(`/login?redirect=${router.asPath}`);
    }
  }, [router, data, loggedIn, fetching, offerEntry]);

  useEffectOnce(() => {
    if (typeof window !== 'undefined') {
      document.body.setAttribute('data-static-bottom-padding', isMobile?.toString());
    }

    return () => {
      document.body.removeAttribute('data-static-bottom-padding');
    };
  });

  if (!fetching && offerEntry) {
    galleryImages = [...(offerEntry?.firstImage as ImageObject[]), ...(offerEntry?.gallery as ImageObject[])];
  } else {
    return <ErrorPage statusCode={404} err={error} />;
  }

  const showLocationSection =
    (!!offerEntry?.locations?.length &&
      !(
        offerEntry?.locations?.length === 1 &&
        (!offerEntry.locations?.[0]?.address || offerEntry.locations?.[0]?.address?.address === 'Online') &&
        !offerEntry.locations?.[0]?.phone
      )) ||
    isMobile;

  return (
    <>
      <div className={styles.offer} data-testid="offers">
        <HeroImage image={galleryImages?.[0]?.webp} />
        <div className={styles.inner}>
          <h1>{offerEntry?.title}</h1>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          <p className={styles.offerDiscount}>
            {offerEntry?.description && <CouponIcon />}
            {offerEntry?.description}
          </p>
          <div className={styles.content}>
            <div className={styles.imageContainer}>
              {!!galleryImages?.length ? (
                <GalleryCarousel images={galleryImages} video={offerEntry?.youtubeVid} />
              ) : (
                <PlaceHolderImage />
              )}
            </div>
            <div className={styles.claimOffer}>
              <ClaimOfferBlock offer={offerEntry} />
              {!isMobile && <ShareBlock shareOptions={offerEntry?.shareOptions} />}
              {!isMobile && offerEntry?.locations?.[0]?.website && (
                <div className={styles.website}>
                  <Button
                    href={offerEntry.locations[0].website}
                    target="_blank"
                    rel="noreferrer noopener"
                    design="highlight-borders"
                    size="small"
                  >
                    Visit website
                  </Button>
                </div>
              )}
            </div>
            <div>
              {offerEntry?.body && (
                <div className={styles.body} dangerouslySetInnerHTML={{ __html: offerEntry?.body }} />
              )}
              {offerEntry?.expiryDate && (
                <h3 className={styles.expiryDate} data-testid="date">
                  Offer expiry: {format(new Date(offerEntry.expiryDate), 'EEEE do MMMM yyyy')}
                </h3>
              )}
              {!!offerEntry?.subBody && <div dangerouslySetInnerHTML={{ __html: offerEntry.subBody }} />}
            </div>
          </div>
          {!!offerEntry?.downloads?.length && (
            <div className={styles.downloads}>
              <h3>Downloads</h3>
              {offerEntry.downloads?.map((f: Maybe<AssetFragment>) => (
                <a href={f!.url!} key={f!.slug}>
                  <DownloadIcon /> {f?.title}
                </a>
              ))}
            </div>
          )}
          {isMobile && <ShareBlock shareOptions={offerEntry?.shareOptions} />}
          {showLocationSection && (
            <div className={styles.locations}>
              <h3>Locations where {isMobile ? 'you can claim' : 'this offer can be claimed'}</h3>
              <div className={styles.entries}>
                {offerEntry?.locations?.map((location: Maybe<LocationFragment>) => (
                  <LocationCard key={location?.description} location={location!} />
                ))}
              </div>
            </div>
          )}
          {!!similarEntries.length && (
            <div className={styles.similar}>
              <h3>Similar items</h3>
              <div className={styles.entries}>{similarEntries.map((entry: JSX.Element) => entry)}</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) =>
  await getServerSidePropsWithGlobalData(ctx, async (ctx) => {
    const { urqlClient, nextCtx, stores } = ctx;
    const slug = Array.isArray(nextCtx?.query?.slug) ? nextCtx?.query?.slug[0] : nextCtx?.query?.slug || '';
    const category = Array.isArray(nextCtx?.query?.category)
      ? nextCtx?.query?.category[0]
      : nextCtx?.query?.category || '';

    const { data } = await urqlClient
      .query<OfferQuery, OfferQueryVariables>(query, {
        slug,
        exclusive: stores.userStore.exclusive,
      })
      .toPromise();

    const offerEntry = extractFragment<OfferFragment>(data?.entry);
    const interestEntries = extractItems<InterestFragment>(offerEntry?.relatedInterests);
    const interests = interestEntries?.map((i: Maybe<InterestFragment>) => i?.slug).toString() || '*';

    await urqlClient
      .query<RelatedEntriesQuery, RelatedEntriesQueryVariables>(relatedQuery, {
        slug: ['not', slug],
        exclusive: stores.userStore.exclusive,
        search: `${offerEntry?.title?.split(' ')?.join(' OR ')} ${category && ` OR categories:${category}`} ${
          interests && ` OR relatedInterests:${interests}`
        }`,
      })
      .toPromise();

    return { props: undefined };
  });

export default withUrql(Offer);
